import { useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { ValidationSchema } from "../../containers/StoreValidation/ValidationSchema";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import usePermission from "../../utils/usePermission";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

export const useStoreView = () => {
  const dispatch = useDispatch();

  const { addToast } = useToasts();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const navigate = useNavigate();
  const [storeDetails, setStoreDetails] = useState({
    StoreDetails: {
      store_address: {
        locality: "",
        street: "",
        city: "",
        state: "",
        area_code: "",
        latitude: "",
        longitude: "",
      },
    },
    StoreSettings: {},
    ServiceabilityAndDeliveryCharges: {},
    BusinessDetails: {},
    BankDetails: {},
    CustomerSupport: {
      gro_address: {
        locality: "",
        street: "",
        city: "",
        state: "",
        area_code: "",
        latitude: "",
        longitude: "",
      },
    },
  });

  const canEditSettings = usePermission("SETTINGS_EDIT");

  const [activeTab, setActiveTab] = useState("StoreDetails");
  useEffect(() => {
    setActiveTab("StoreDetails");
  }, []);

  const [isStoreDetailsSaved, setIsStoreDetailsSaved] = useState(false);
  const [errors, setErrors] = useState({});

  const [storeImages, setStoreImages] = useState([]);
  const [initialStoreImages, setInitialStoreImages] = useState([]);
  const [storeImagesLength, setStoreImagesLength] = useState(
    storeImages?.length || 1,
  );

  const [isStoreInitialized, setIsStoreInitialized] = useState(false);

  const fetchData = async (tab) => {
    try {
      const result = await apiHandler({
        url: `${endpoint.GET_STORE_DETAILS}/${tab.replace(/\s/g, "")}`,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      if (result?.data) {
        setStoreDetails((prevDetails) => ({
          ...prevDetails,
          [tab]: result.data,
        }));

        if (tab === "StoreDetails") {
          const hasStoreData = Object.values(result.data || {}).some(
            (val) =>
              val !== "" &&
              val !== null &&
              !(Array.isArray(val) && val.length === 0),
          );

          setIsStoreInitialized(hasStoreData);

          console.log("StoreDetails fetched:", result.data);
          if (result.data?.bpp_images) {
            const images = result.data?.bpp_images || [];

            setStoreImages([...images]);
            setInitialStoreImages([...images]);
          } else {
            setStoreImages([]);
            setInitialStoreImages([]);
          }
        }
      } else {
        //console.error(`No data found for ${tab}.`);
      }
    } catch (error) {
      //console.error(`Error fetching data for ${tab}:`, error);
    }
  };

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab]);

  const handleTabSwitch = (tab) => {
    if (isTabDisabled(tab)) {
      addToast(`${tab} is disabled until StoreDetails is completed.`, {
        appearance: "warning",
      });
      return;
    }
    setActiveTab(tab);
    // fetchData(tab);
  };

  const isTabDisabled = (tab) => {
    if (!isStoreInitialized && !isStoreDetailsSaved) {
      return tab !== "StoreDetails";
    }
    return false;
  };

  const handleBlur = (tab, field, value) => {
    const schema = ValidationSchema[tab];
    if (!schema) return;
    const path = field.split(".");

    const buildPayload = (path, value) => {
      let result = {};
      let current = result;

      path.forEach((segment, index) => {
        if (index === path.length - 1) {
          current[segment] = value;
        } else {
          current[segment] = {};
          current = current[segment];
        }
      });

      return result;
    };

    const payload = buildPayload(path, value);

    const { error } = schema.validate(payload, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (error) {
      const fieldError = error.details.find(
        (err) => err.context.label === field,
      );

      setErrors((prevErrors) => {
        const latestError = {
          ...prevErrors,
        };
        latestError[field] = fieldError ? fieldError.message : "";
        return latestError;
      });
    } else {
      setErrors((prevErrors) => {
        const { [field]: _, ...rest } = prevErrors;
        return rest;
      });
    }
  };

  const urlToFile = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl, { mode: "cors" });
      const blob = await response.blob();
      const filename = imageUrl.split("/").pop();
      return new File([blob], filename, { type: blob.type });
    } catch (error) {
      //console.error("Error converting image URL to File:", error);
      return null;
    }
  };

  const handleFormSubmit = async (tab) => {
    if (!canEditSettings) {
      addToast("You do not have permission to edit settings.", {
        appearance: "error",
        autoDismiss: true,
      });
      return;
    }

    try {
      let formData = null;
      let payload = null;

      switch (tab) {
        case "StoreDetails":
          const storeData = storeDetails.StoreDetails || {};

          formData = new FormData();
          formData.append("id", storeData.id || 0);
          formData.append("bpp_name", storeData.bpp_name || "");
          formData.append(
            "store_address",
            JSON.stringify(storeData.store_address || {}),
          );
          formData.append("store_active", storeData.store_active || false);
          formData.append("bpp_symbol", storeData.bpp_symbol || "");
          formData.append(
            "store_opening_time",
            storeData.store_opening_time || "",
          );
          formData.append(
            "store_closing_time",
            storeData.store_closing_time || "",
          );

          // Only send newly added File objects. Existing image URLs (strings)
          // should not be re-converted and re-uploaded as files — that causes
          // duplicate images on the server when saving without changes.
          const imageFiles = storeImages.filter((img) => img instanceof File);

          imageFiles.forEach((file) => {
            if (file) formData.append("bpp_images", file);
          });

          // Also send the list of remaining existing image URLs so the backend
          // can remove any images that the user deleted from the UI.
          const existingImageURLs = storeImages.filter(
            (img) => typeof img === "string"
          );

          // count occurrences in initial array
          const initialCounts = {};
          initialStoreImages.forEach((url) => {
            initialCounts[url] = (initialCounts[url] || 0) + 1;
          });
          console.log("Initial store images:", initialCounts);

          // count occurrences currently
          const existingCounts = {};
          existingImageURLs.forEach((url) => {
            existingCounts[url] = (existingCounts[url] || 0) + 1;
          });
          console.log("Existing image URLs being sent to backend:", existingCounts);

          // determine deletions
          const deletedImageURLs = [];

          Object.keys(initialCounts).forEach((url) => {
            const initialCount = initialCounts[url];
            const existingCount = existingCounts[url] || 0;

            const deleteCount = initialCount - existingCount;

            for (let i = 0; i < deleteCount; i++) {
              deletedImageURLs.push(url);
            }
          });

          console.log("Deleted image URLs being sent to backend:", deletedImageURLs);

          formData.append(
            "deleted_bpp_images",
            JSON.stringify(deletedImageURLs)
          );

          formData.append("bpp_short_desc", storeData.bpp_short_desc || "");
          formData.append("bpp_long_desc", storeData.bpp_long_desc || "");
          formData.append("store_email", storeData.store_email || "");
          formData.append(
            "store_phone_number",
            storeData.store_phone_number || "",
          );
          break;

        case "StoreSettings":
          const storeSettings = storeDetails.StoreSettings || {};

          payload = {
            id: storeSettings.id || 0,
            buyer_finder_fee_min: storeSettings.buyer_finder_fee_min || 0,
            buyer_finder_fee_max: storeSettings.buyer_finder_fee_max || 100,
            return_window: storeSettings.return_window || 0,
            delivery_charges: storeSettings.delivery_charges || 0,
            delivery_tat: storeSettings.delivery_tat || 0,
            is_products_returnable:
              storeSettings.is_products_returnable || false,
            is_seller_pickup_return:
              storeSettings.is_seller_pickup_return || false,
            is_products_cancellable:
              storeSettings.is_products_cancellable || false,
            is_tracking_available: storeSettings.is_tracking_available || false,
            // is_all_india_serviceable:
            //   storeSettings.is_all_india_serviceable || false,
          };
          break;

        case "ServiceabilityAndDeliveryCharges":
          const serviceability =
            storeDetails.ServiceabilityAndDeliveryCharges || {};

          payload = {
            delivery_charges: serviceability.delivery_charges || 0,
            is_all_india_serviceable:
              serviceability.is_all_india_serviceable || false,
            id: serviceability.id,
          };
          break;

        case "BusinessDetails":
          const businessDetails = storeDetails.BusinessDetails || {};

          payload = {
            id: businessDetails.id || 0,
            authorised_signatory: businessDetails.authorised_signatory || "",
            date_of_incorporation: businessDetails.date_of_incorporation || "",
            store_pan_number: businessDetails.store_pan_number || "",
            name_as_per_pan: businessDetails.name_as_per_pan || "",
            store_gst_number: businessDetails.store_gst_number || "",
          };
          break;

        case "BankDetails":
          const bankDetails = storeDetails.BankDetails || {};

          payload = {
            id: bankDetails.id || 0,
            beneficiary_name:
              bankDetails.store_bank_details?.beneficiary_name || "",
            settlement_bank_account_no:
              bankDetails.store_bank_details?.settlement_bank_account_no || "",
            settlement_ifsc_code:
              bankDetails.store_bank_details?.settlement_ifsc_code || "",
            bank_name: bankDetails.store_bank_details?.bank_name || "",
            branch_name: bankDetails.store_bank_details?.branch_name || "",
            settlement_window:
              bankDetails.store_bank_details?.settlement_window || null,
          };
          break;

        case "CustomerSupport":
          const supportData = storeDetails.CustomerSupport || {};

          formData = new FormData();
          formData.append("id", supportData.id || 0);
          formData.append("support_email", supportData.support_email || "");
          formData.append(
            "support_phone_number",
            supportData.support_phone_number || "",
          );
          formData.append("gro_name", supportData.gro_name || "");
          formData.append("gro_phone", supportData.gro_phone || "");
          formData.append("gro_email", supportData.gro_email || "");
          formData.append("gro_active", supportData.gro_active || false);
          formData.append(
            "gro_address",
            JSON.stringify(supportData.gro_address || {}),
          );
          formData.append(
            "gro_profile_image",
            supportData.gro_profile_image || "",
          );
          break;

        default:
          addToast("Unknown tab selected. Please try again.", {
            appearance: "error",
          });
          return;
      }

      const schema = ValidationSchema[tab];

      if (!schema) return;

      const dataToValidate =
        formData && (tab === "StoreDetails" || tab === "CustomerSupport")
          ? (() => {
            const parsedData = Object.fromEntries(formData.entries());
            if (parsedData.gro_address) {
              try {
                parsedData.gro_address = JSON.parse(parsedData.gro_address);
              } catch (err) {
                //console.error("Error parsing gro_address:", err);
              }
            }
            if (parsedData.store_address) {
              try {
                parsedData.store_address = JSON.parse(
                  parsedData.store_address,
                );
              } catch (err) {
                //console.error("Error parsing store_address:", err);
              }
            }

            return parsedData;
          })()
          : payload;

      const { error } = schema.validate(dataToValidate, {
        abortEarly: false,
        allowUnknown: true,
      });

      if (error) {
        const tabErrors = error.details.reduce((acc, err) => {
          const path = err.path.join(".");

          acc[path] = err.message;

          return acc;
        }, {});

        setErrors(tabErrors);

        setErrors(tabErrors);
        return;
      }
      let details;
      if (tab === "BankDetails") {
        details = {
          beneficiary_name: payload.beneficiary_name || "",
          settlement_bank_account_no: payload.settlement_bank_account_no || "",
          settlement_window: payload.settlement_window || null,
          settlement_ifsc_code: payload.settlement_ifsc_code || "",
          bank_name: payload.bank_name || "",
          branch_name: payload.branch_name || "",
        };
      }

      const apiBuffer =
        tab === "StoreDetails" || tab === "CustomerSupport"
          ? {
            url: `${endpoint.UPDATE_STORE_DETAILS}/${tab}`,
            method: "POST",
            authToken: authData,
            dispatch,
            storeId,
            headers: { "Content-Type": "multipart/form-data" },
            data: formData,
          }
          : tab === "BankDetails"
            ? {
              url: `${endpoint.UPDATE_STORE_DETAILS}/${tab}`,

              method: "POST",
              authToken: authData,
              dispatch,
              storeId,
              data: { store_bank_details: details, id: payload.id || 0 },
            }
            : {
              url: `${endpoint.UPDATE_STORE_DETAILS}/${tab}`,

              method: "POST",
              authToken: authData,
              dispatch,
              storeId,
              data: payload,
            };

      const result = await apiHandler(apiBuffer);
      addToast(`${tab} saved successfully!`, { appearance: "success" });

      if (tab === "StoreDetails") {
        setIsStoreDetailsSaved(true);

        const updatedExistingImages = storeImages.filter(
          (img) => typeof img === "string"
        );

        setInitialStoreImages([...updatedExistingImages]);

        try {
          fetchData(tab);
        } catch (e) {
          window.location.href = "/analytics";
        }
      }
      // setTabCompletionStatus((prevStatus) => ({
      //   ...prevStatus,
      //   [tab]: true,
      // }));
    } catch (error) {
      //console.error("Error updating store details:", error);
      addToast("Error updating store details. Please try again.", {
        appearance: "error",
      });
    }
  };
  const renderErrorMessage = (fieldName) => {
    return errors[fieldName] ? <span>{errors[fieldName]}</span> : null;
  };

  const handleStoreDetailChange = (tab, field, value) => {
    setStoreDetails((prevDetails) => ({
      ...prevDetails,
      [tab]: { ...prevDetails[tab], [field]: value },
    }));
    handleBlur(tab, field, value);
  };
  const handleStoreLogoChange = (file) => {
    if (!file) return;

    handleStoreDetailChange("StoreDetails", "bpp_symbol", file);
    handleBlur("StoreDetails", "bpp_symbol", file);
  };

  const addInputField = () => {
    setStoreImages((prevImages) =>
      Array.isArray(prevImages) ? [...prevImages, null] : [null],
    );
  };

  const removeInputField = (index) => {
    setStoreImages((prevImages) => {
      const updatedImages = [...prevImages];
      updatedImages.splice(index, 1);
      return updatedImages;
    });
  };

  const handleImageChange = (file) => {
    if (!file) return;

    // Prevent uploading the same image again. Compare by filename,
    // and for File objects also compare size and type for stronger match.
    const fileName = file.name;

    const isDuplicate = storeImages.some((img) => {
      if (typeof img === "string") {
        const urlName = img.split("/").pop();
        return urlName === fileName;
      }
      if (img instanceof File) {
        return (
          img.name === file.name && img.size === file.size && img.type === file.type
        );
      }
      return false;
    });

    if (isDuplicate) {
      addToast("This image already exists. Please choose a different image.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    setStoreImages((prev) => [...prev, file]);
  };

  const handleUpdateClick = () => {
    document.getElementById(`imageInputNew`).click();
  };

  const handleAddressChange = (parentKey, field, value) => {
    setStoreDetails((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        store_address: {
          ...prev[parentKey].store_address,
          [field]: value,
        },
      },
    }));
  };

  const handleStoreSettings = (tab, field, value) => {
    setStoreDetails((prevState) => ({
      ...prevState,
      [tab]: {
        ...prevState[tab],
        [field]: value,
      },
    }));

    handleBlur(tab, field, value);
  };

  const handleBusinessDetails = (tab, field, value) => {
    setStoreDetails((prevState) => ({
      ...prevState,
      [tab]: {
        ...prevState[tab],
        [field]: value,
      },
    }));
    handleBlur(tab, field, value);
  };

  const handleBankDetails = (tab, field, value) => {
    setStoreDetails((prevState) => ({
      ...prevState,
      [tab]: {
        ...prevState[tab],
        store_bank_details: {
          ...prevState[tab]?.store_bank_details,
          [field]: value,
        },
      },
    }));
    handleBlur(tab, field, value);
  };

  const handleServiceabilityDetails = (tab, field, value) => {
    setStoreDetails((prevState) => ({
      ...prevState,
      [tab]: {
        ...prevState[tab],
        [field]: value,
      },
    }));
    handleBlur(tab, field, value);
  };

  const handleCustomerSupport = (parentKey, fieldKey, value) => {
    if (fieldKey.startsWith("gro_address.")) {
      const addressField = fieldKey.split(".")[1];
      setStoreDetails((prevDetails) => ({
        ...prevDetails,
        [parentKey]: {
          ...prevDetails[parentKey],
          gro_address: {
            ...prevDetails[parentKey]?.gro_address,
            [addressField]: value,
          },
        },
      }));
    } else {
      setStoreDetails((prevDetails) => ({
        ...prevDetails,
        [parentKey]: {
          ...prevDetails[parentKey],
          [fieldKey]: value,
        },
      }));
    }
  };

  const handleGroProfileImageChange = (file) => {
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPEG, PNG, JPG, and WEBP files are allowed.");
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("File size must be less than 2MB.");
      return;
    }

    handleCustomerSupport("CustomerSupport", "gro_profile_image", file);
    handleBlur("CustomerSupport", "gro_profile_image", file);
  };

  return {
    storeDetails,
    setStoreDetails,
    activeTab,
    setActiveTab: handleTabSwitch,
    // isTabDisabled,
    storeImages,
    storeImagesLength,
    fetchData,
    addInputField,
    removeInputField,
    handleImageChange,
    handleUpdateClick,
    handleStoreLogoChange,
    handleAddressChange,
    handleStoreSettings,
    handleServiceabilityDetails,
    handleBusinessDetails,
    handleBankDetails,
    handleCustomerSupport,
    handleFormSubmit,
    handleBlur,
    handleStoreDetailChange,
    renderErrorMessage,
    errors,
    handleGroProfileImageChange,
    isTabDisabled,
  };
};
